import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  lastValueFrom,
  Observable,
  pairwise,
  startWith,
  Subscription,
} from 'rxjs';
import { GeneralSnackComponent } from '../components-generals/general-snack/general-snack.component';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  public URL_API = environment.apiURLNew;
  public emitFilterLocation = new EventEmitter<{
    Sort?: string;
    SortDir?: string;
    SearchField?: string;
    SearchFieldValue?: string;
  }>();
  public emitFilterTiket = new EventEmitter<{ order: any; sort: any }>();
  public searchKey = new EventEmitter<string>();

  constructor(
    private router: Router,
    private http: HttpClient,
    private matSnackBar: MatSnackBar,
  ) {}

  public buildParams(filters: any): HttpParams {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params = params.set(key, value.toString());
      }
    });
    return params;
  }

  public buildParamsEncoder(filters: Record<string, any>): HttpParams {
    const encoder: import('@angular/common/http').HttpParameterCodec = {
      encodeKey: (key: string) => key,
      encodeValue: (value: string) => encodeURIComponent(value),
      decodeKey: (key: string) => key,
      decodeValue: (value: string) => decodeURIComponent(value),
    };

    let params = new HttpParams({ encoder });

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            params = params.append(key, String(v));
          });
        } else if (typeof value === 'object') {
          params = params.set(key, JSON.stringify(value));
        } else {
          params = params.set(key, String(value));
        }
      }
    });

    return params;
  }
  public buildParamsString(filters: any): HttpParams {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value.toString());
      }
    });
    return params;
  }

  public async getServices<T extends readonly [...Observable<any>[]]>(
    observables: T,
  ): Promise<{ [K in keyof T]: T[K] extends Observable<infer R> ? R : never }> {
    return (await lastValueFrom(combineLatest(observables))) as any;
  }

  public goToUrl(
    route: string[],
    queryParams: { [key: string]: string | number },
    newParas?: boolean,
  ): void {
    window.scrollTo({ top: 0 });
    newParas
      ? this.router.navigate(route, {
          queryParams,
        })
      : this.router.navigate(route, {
          queryParams,
          queryParamsHandling: 'merge',
        });
  }

  public subscribeForm(formGroup: FormGroup | FormControl): Observable<[any, any]> {
    return formGroup.valueChanges.pipe(
      startWith(formGroup.value), // Emitir el valor inicial
      debounceTime(700), // Esperar 700 ms tras cada cambio
      distinctUntilChanged(), // Ignorar valores idénticos
      pairwise(), // Obtener valor anterior y actual
      filter(([prev, current]) => JSON.stringify(prev) !== JSON.stringify(current)), // Validar si realmente cambió
    );
  }

  public subscribeFormSimple(
    formGroup: FormGroup | FormControl | AbstractControl,
  ): Observable<any> {
    return formGroup.valueChanges.pipe(
      startWith(formGroup.value), // Emitir el valor inicial
      debounceTime(700), // Esperar 700 ms tras cada cambio
      distinctUntilChanged(), // Ignorar valores idénticos
    );
  }

  public unsubscribeAll(subscribeArray: Subscription[]): void {
    subscribeArray.forEach((sub) => sub.unsubscribe());
  }

  // Trasforma un Json a string
  public jsonToString(data: any): string {
    return JSON.stringify(data);
  }
  // Trasforma un string a Json
  public stringToJson(jsonString: string | null): any {
    try {
      return JSON.parse(jsonString ?? '');
    } catch (error) {
      return null;
    }
  }

  // Muestra una Alerta y un texto ( normal , error y alerta)
  public async viewSnacAlert(text: string, type: 'normal' | 'error' | 'alert', tilule: string) {
    this.matSnackBar.openFromComponent(GeneralSnackComponent, {
      duration: 5000,
      panelClass: ['panel-snack-general'],
      data: {
        type: type,
        icon: 'close',
        text: tilule,
        suptext: text,
        body: true,
      },
    });
  }

  public getEstadosSepomex(): Observable<
    {
      description: string;
      id: number;
      value: string;
    }[]
  > {
    return this.http.get<
      {
        description: string;
        id: number;
        value: string;
      }[]
    >(`${environment.apiURLNew}/getEstadosSepomex`);
  }
}
